
import axios from 'axios';
import { SecureStorage } from '../storage/SecureStorage';
import { API_BASE } from '../config';

// ---- In‑memory state to avoid reading the plugin on each request ----
let accessTokenMem: string | null = null;
let refreshTokenMem: string | null = null;

// Callback that your app can register to handle global logout events
let onLogoutCb: (() => void) | null = null;
export function registerOnLogout(cb: () => void) { onLogoutCb = cb; }

// API helper specific to the trainer app: cancel a booking by ID
export async function cancelBookingById(bookingId: number) {
    const r = await api.delete(`/api/mobile/booking/${bookingId}`);
    return r.data;
}

// Helpers to set/restore/clear tokens
export async function setAuthFromLogin(loginResponse: any) {
    const { accessToken, refreshToken, user } = loginResponse;
    accessTokenMem = accessToken;
    refreshTokenMem = refreshToken;
    await SecureStorage.saveTokens(accessToken, refreshToken);
    if (user) {
        await SecureStorage.saveUser(user);
    }
}

export async function restoreAuthFromStorage() {
    const { access, refresh } = await SecureStorage.getTokens();
    const user = await SecureStorage.getUser();
    accessTokenMem = access;
    refreshTokenMem = refresh;
    return { access, refresh, user };
}

export async function clearAuth() {
    accessTokenMem = null;
    refreshTokenMem = null;
    await SecureStorage.clear();
    if (onLogoutCb) onLogoutCb();
}

// ---- Axios base instance ----
const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
});

// ---- Request interceptor: attach Bearer access token ----
api.interceptors.request.use(
    (config: any) => {
        config.headers = config.headers || {};
        if (accessTokenMem) {
            config.headers.Authorization = `Bearer ${accessTokenMem}`;
        }
        return config;
    },
    (error: any) => Promise.reject(error)
);

// ---- Refresh management with subscription queue ----
let refreshing = false;
let subscribers: ((token: string | null) => void)[] = [];
function subscribeTokenRefresh(cb: (t: string | null) => void) {
    subscribers.push(cb);
}
function notifySubscribers(token: string | null) {
    subscribers.forEach(cb => cb(token));
    subscribers = [];
}

async function refreshTokens(): Promise<string | null> {
    if (!refreshTokenMem) {
        const { refresh } = await SecureStorage.getTokens();
        refreshTokenMem = refresh;
    }
    if (!refreshTokenMem) return null;
    try {
        const resp = await axios.post(`${API_BASE}/api/professionals/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshTokenMem}` }
        });

        const { accessToken, refreshToken, user } = (resp.data || {}) as any;
        if (!accessToken || !refreshToken) return null;
        accessTokenMem = accessToken;
        refreshTokenMem = refreshToken;
        await SecureStorage.saveTokens(accessToken, refreshToken);
        if (user) {
            await SecureStorage.saveUser(user);
        }
        return accessToken;
    } catch {
        return null;
    }
}

// ---- Response interceptor: 401 → attempt refresh ----
api.interceptors.response.use(
    (res: any) => res,
    async (error: any) => {
        const status = error.response?.status;
        const original = error.config as any;
        if (status !== 401 || original?._retry) {
            return Promise.reject(error);
        }
        original._retry = true;
        if (refreshing) {
            return new Promise((resolve, reject) => {
                subscribeTokenRefresh((token) => {
                    if (!token) return reject(error);
                    original.headers = original.headers ?? {};
                    original.headers.Authorization = `Bearer ${token}`;
                    resolve(api(original));
                });
            });
        }
        refreshing = true;
        const newAccess = await refreshTokens();
        refreshing = false;
        notifySubscribers(newAccess);
        if (!newAccess) {
            await clearAuth();
            return Promise.reject(error);
        }
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
    }
);

export default api;
